import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, UserCog, Mail, Phone, Building2, Edit3, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { usePageExport } from "@/context/ExportContext";

const BLANK = { name: "", email: "", department_id: "", role_title: "", phone: "", active: true, notes: "", level: "L1" };

const LEVEL_BADGE = {
  L1: "bg-indigo-50 text-indigo-700 border-indigo-200",
  L2: "bg-amber-50 text-amber-700 border-amber-200",
  L3: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function POCs() {
  const [pocs, setPocs] = useState([]);
  const [depts, setDepts] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/pocs").then((r) => setPocs(r.data));

  useEffect(() => {
    load();
    api.get("/departments").then((r) => setDepts(r.data.filter((d) => d.active)));
  }, []);

  const deptName = (id) => depts.find((d) => d.id === id)?.name || "—";

  const filtered = useMemo(() => {
    if (!q.trim()) return pocs;
    const s = q.toLowerCase();
    return pocs.filter((p) =>
      [p.name, p.email, p.role_title, deptName(p.department_id)].some((v) => (v || "").toLowerCase().includes(s))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, pocs, depts]);

  const exportColumns = useMemo(() => [
    { key: "name", header: "Name" },
    { key: "email", header: "Email" },
    { key: "role_title", header: "Role" },
    { key: "department_name", header: "Department" },
    { key: "phone", header: "Phone" },
    { key: "active", header: "Active" },
  ], []);
  const exportRows = filtered.map((p) => ({ ...p, department_name: deptName(p.department_id) }));
  usePageExport({ filename: "kippu-pocs.csv", rows: exportRows, columns: exportColumns, label: "POCs" });

  const startEdit = (poc) => {
    setEditingId(poc.id);
    setForm({
      name: poc.name, email: poc.email, department_id: poc.department_id || "",
      role_title: poc.role_title || "", phone: poc.phone || "",
      active: poc.active !== false, notes: poc.notes || "",
      level: poc.level || "L1",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name || !form.email) return toast.error("Name and email required");
    try {
      const payload = { ...form };
      if (!payload.department_id) delete payload.department_id;
      if (editingId) {
        await api.patch(`/pocs/${editingId}`, payload);
        toast.success("POC updated");
      } else {
        await api.post("/pocs", payload);
        toast.success("POC added");
      }
      setOpen(false); setForm(BLANK); setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this POC?")) return;
    try {
      await api.delete(`/pocs/${id}`);
      toast.success("POC removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="pocs-page">
      <Card className="border-slate-200 bg-indigo-50/40 border-indigo-100">
        <CardContent className="p-4 text-xs text-indigo-900 flex items-start gap-2">
          <UserCog className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            <strong>Department Points-of-Contact.</strong> All operational emails are routed by POC level —
            <strong> L1</strong> receives ticket creations + reminders · <strong>L2</strong> receives SLA breach
            escalations · <strong>L2 + L3</strong> receive daily / weekly / monthly reports. Customers never
            receive system emails.
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email, role, or department..."
              className="pl-9"
              data-testid="pocs-search"
            />
          </div>
          <Button
            onClick={() => { setEditingId(null); setForm(BLANK); setOpen(true); }}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="add-poc-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add POC
          </Button>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="text-xs uppercase tracking-wider text-slate-500">
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Level</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.id} className="hover:bg-slate-50" data-testid={`poc-row-${p.id}`}>
                  <TableCell className="font-medium text-sm text-slate-800">{p.name}</TableCell>
                  <TableCell className="text-sm text-slate-700">
                    <span className="inline-flex items-center gap-1.5">
                      <Mail className="w-3 h-3 text-slate-400" />
                      {p.email}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">
                    <span className="inline-flex items-center gap-1.5">
                      <Building2 className="w-3 h-3 text-slate-400" />
                      {deptName(p.department_id)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] font-semibold ${LEVEL_BADGE[p.level || "L1"]}`} data-testid={`poc-level-${p.id}`}>
                      {p.level || "L1"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{p.role_title || "—"}</TableCell>
                  <TableCell className="text-sm font-mono text-slate-600">{p.phone || "—"}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] ${
                      p.active !== false
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-slate-100 text-slate-500 border-slate-200"
                    }`}>
                      {p.active !== false ? "Active" : "Paused"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(p)} data-testid={`poc-edit-${p.id}`}>
                        <Edit3 className="w-3.5 h-3.5 text-slate-500" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(p.id)} data-testid={`poc-delete-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-sm text-slate-400 py-12">No POCs found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="poc-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingId ? "Edit POC" : "Add POC"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="poc-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="poc-email-input" />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs">Department</Label>
              <Select value={form.department_id || ""} onValueChange={(v) => setForm({ ...form, department_id: v })}>
                <SelectTrigger data-testid="poc-dept-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  {depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Role title</Label>
              <Input value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} placeholder="Finance Lead" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Level *</Label>
              <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                <SelectTrigger data-testid="poc-level-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="L1">L1 — Ticket creation · reminders</SelectItem>
                  <SelectItem value="L2">L2 — SLA breach · reports</SelectItem>
                  <SelectItem value="L3">L3 — Org-level reports</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+974 ..." />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs">Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional context" />
            </div>
            <div className="col-span-2 flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="poc-active-switch" />
              <span className="text-xs text-slate-600">{form.active ? "Active — will receive notifications" : "Paused"}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setEditingId(null); }}>Cancel</Button>
            <Button onClick={submit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="poc-submit-btn">
              {editingId ? "Save changes" : "Add POC"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
